<template>
  <div>
    <a-modal
      :visible="visible"
      title="Edit Discipline Record"
      okText="Update"
      cancelText="Cancel"
      :confirmLoading="submitting"
      @ok="handleSubmit"
      @cancel="handleCancel"
      width="700px"
    >
      <a-form :form="form" layout="vertical">
        <!-- Session & Class -->
        <div class="row">
          <div class="col-md-6 col-12">
            <a-form-item label="Session">
              <a-select
                placeholder="Select Session"
                v-decorator="['session_id', { rules: [{ required: true, message: 'Please select a session' }] }]"
                @change="handleSessionChange"
              >
                <a-select-option
                  v-for="s in sessions"
                  :key="s.id"
                  :value="s.id"
                >
                  {{ s.name }}
                  <small v-if="s.is_current === 1" class="text-primary ml-1">(Current)</small>
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>

          <div class="col-md-6 col-12">
            <a-form-item label="Class">
              <a-select
                placeholder="Select Class"
                v-decorator="['class_id', { rules: [{ required: true, message: 'Please select a class' }] }]"
                @change="handleClassChange"
              >
                <a-select-option
                  v-for="c in classes"
                  :key="c.id"
                  :value="c.id"
                >
                  {{ c.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </div>
        </div>

        <!-- Student -->
        <a-form-item label="Student">
          <a-select
            show-search
            placeholder="Select Student"
            :filterOption="false"
            @search="onSearchStudent"
            @popupScroll="onScrollStudent"
            v-decorator="['student_id', { rules: [{ required: true, message: 'Please select a student' }] }]"
            :loading="studentLoading"
          >
            <a-select-option
              v-for="student in students"
              :key="student.id"
              :value="student.id"
            >
              {{ student.first_name }} {{ student.last_name }}
              <span v-if="student.enrollment && student.enrollment.length > 0" class="text-muted ml-1">
                [{{ student.enrollment[0].school_class ? student.enrollment[0].school_class.name : '' }},
                 {{ student.enrollment[0].section ? student.enrollment[0].section.name : '' }},
                 {{ student.enrollment[0].roll_no ? 'Roll: ' + student.enrollment[0].roll_no : '' }}]
              </span>
            </a-select-option>

            <template slot="notFoundContent">
              <a-spin v-if="studentLoading" size="small" />
              <span v-else>No students found</span>
            </template>
          </a-select>
        </a-form-item>

        <!-- Rest of your form remains the same -->
        <a-form-item label="Incident Date">
          <a-date-picker
            style="width: 100%"
            :format="dateFormat"
            v-decorator="[
              'incident_date',
              { rules: [{ required: true, message: 'Please pick a date' }] }
            ]"
          />
        </a-form-item>

        <a-form-item label="Category">
          <a-select
            placeholder="Select Category"
            v-decorator="['category_id']"
          >
            <a-select-option v-for="c in categories" :key="c.id" :value="c.id">
              {{ c.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Description">
          <a-textarea
            rows="3"
            v-decorator="['description', { rules: [{ required: true, message: 'Please enter description' }] }]"
          />
        </a-form-item>

        <a-form-item>
          <a-checkbox v-model="showPenaltyFields">Mark as Penalty</a-checkbox>
        </a-form-item>

        <template v-if="showPenaltyFields">
          <a-form-item label="Penalty Type">
            <a-select placeholder="Select Penalty" v-decorator="['penalty_id']">
              <a-select-option
                v-for="p in penalties"
                :key="p.id"
                :value="p.id"
              >
                {{ p.name }}
              </a-select-option>
            </a-select>
          </a-form-item>

          <a-form-item label="Penalty Details">
            <a-input v-decorator="['penalty_details']" placeholder="Optional details" />
          </a-form-item>
        </template>

        <a-form-item label="Severity">
          <a-radio-group
            v-decorator="[
              'severity',
              { rules: [{ required: true }] }
            ]"
          >
            <a-radio value="minor">Minor</a-radio>
            <a-radio value="medium">Medium</a-radio>
            <a-radio value="major">Major</a-radio>
          </a-radio-group>
        </a-form-item>

        <a-form-item label="Reported By">
          <a-select
            show-search
            option-filter-prop="children"
            placeholder="Select Reporter"
            v-decorator="['reported_by', { rules: [{ required: true, message: 'Please select reporter' }] }]"
          >
            <a-select-option
              v-for="teacher in teachers"
              :key="teacher.id"
              :value="teacher.id"
            >
              {{ teacher.first_name }} {{ teacher.last_name }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'EditDisciplineForm',
  props: {
    record: { type: Object, required: true },
    visible: { type: Boolean, default: false },
  },
  data() {
    return {
      submitting: false,
      dateFormat: 'YYYY-MM-DD',
      showPenaltyFields: false,
      students: [],
      studentLoading: false,
      sessions: [],
      currentSessionId: null,
      studentPagination: { current: 1, last: 1, perPage: 10, total: 0 },
      studentSearch: '',
      categories: [],
      penalties: [],
      teachers: [],
      classes: [],
    }
  },
  created() {
    this.form = this.$form.createForm(this)
  },
  watch: {
    visible(val) {
      if (val) {
        this.setInitialValues()
        this.getSessions()
      } else {
        this.form.resetFields()
        this.students = []
        this.showPenaltyFields = false
      }
    },
    record: {
      immediate: true,
      deep: true,
      handler(val) {
        if (val && Object.keys(val).length > 0) {
          this.loadInitialData()
        }
      },
    },
  },
  methods: {
    moment,

    async loadInitialData() {
      await Promise.all([
        this.getSessions(),
        this.getCategories(),
        this.getPenalties(),
        this.getTeachers(),
        this.getClasses(),
      ])
      this.setInitialValues()
    },

    getSessions() {
      this.$http.get('/api/session')
        .then(({ data }) => {
          this.sessions = data.data
          const current = this.sessions.find(s => s.is_current === 1)
          this.currentSessionId = current ? current.id : null
          this.$nextTick(() => {
            if (this.visible) {
              this.form.setFieldsValue({ session_id: this.currentSessionId })
            }
          })
          this.getClasses(this.currentSessionId)
        })
        .catch(err => console.log(err))
    },

    getCategories() {
      return this.$http.get('/api/student-discipline-category')
        .then(({ data }) => (this.categories = data.data))
    },

    getPenalties() {
      return this.$http.get('/api/discipline-penalty')
        .then(({ data }) => (this.penalties = data.data))
    },

    getTeachers() {
      let teachers = []
      let currentPage = 1
      let totalPages = 1

      this.$http.get('/api/teacher/', { params: { page: currentPage } })
        .then(firstPageResponse => {
          const firstPageData = firstPageResponse.data
          teachers = [...teachers, ...firstPageData.data]

          if (firstPageData.meta && firstPageData.meta.last_page) {
            totalPages = firstPageData.meta.last_page
          }

          if (totalPages > 1) {
            const pagePromises = []
            for (let page = 2; page <= totalPages; page++) {
              pagePromises.push(
                this.$http.get('/api/teacher/', { params: { page } })
              )
            }

            return Promise.all(pagePromises).then(responses => {
              responses.forEach(response => {
                teachers = [...teachers, ...response.data.data]
              })
              return teachers
            })
          } else {
            return teachers
          }
        })
        .then(teachersResult => {
          this.teachers = teachersResult
        })
        .catch(error => {
          console.error('Failed to fetch teachers', error)
          this.$notification.error({
            message: 'Failed to load teachers',
            description: 'Please try again later.',
          })
        })
    },

    getClasses(sessionId) {
      this.$http.get('/api/class', { params: { session_id: sessionId } })
        .then(({ data }) => { this.classes = data.data })
    },

    fetchStudents(search = '', page = 1, classId = null, sessionId = null) {
      this.studentLoading = true
      this.studentSearch = search
      const params = {
        search,
        page,
        per_page: this.studentPagination.perPage,
      }
      if (classId) params.class_id = classId
      if (sessionId) params.session_id = sessionId

      return this.$http
        .get('/api/student', { params })
        .then(({ data }) => {
          const list = data.data
          const meta = data.meta
          this.students = page === 1 ? list : this.students.concat(list)
          this.studentPagination.current = meta.current_page
          this.studentPagination.last = meta.last_page
          this.studentPagination.total = meta.total
        })
        .finally(() => (this.studentLoading = false))
    },

    handleSessionChange(sessionId) {
      this.form.setFieldsValue({ class_id: undefined })
      this.getClasses(sessionId)
    },

    handleClassChange(classId) {
      this.form.setFieldsValue({ student_id: undefined })
      this.students = []
      const sessionId = this.form.getFieldValue('session_id') || this.currentSessionId
      if (classId) {
        this.fetchStudents('', 1, classId, sessionId)
      }
    },

    onSearchStudent(value) {
      const classId = this.form.getFieldValue('class_id')
      const sessionId = this.form.getFieldValue('session_id') || this.currentSessionId
      this.fetchStudents(value, 1, classId, sessionId)
    },

    onScrollStudent(e) {
      const { scrollTop, scrollHeight, clientHeight } = e.target
      if (
        scrollTop + clientHeight >= scrollHeight - 10 &&
        !this.studentLoading &&
        this.studentPagination.current < this.studentPagination.last
      ) {
        const classId = this.form.getFieldValue('class_id')
        const sessionId = this.form.getFieldValue('session_id') || this.currentSessionId

        this.fetchStudents(
          this.studentSearch,
          this.studentPagination.current + 1,
          classId,
          sessionId
        )
      }
    },

    async setInitialValues() {
      if (!this.record) return
      const r = this.record
      console.log(r)
      let studentFound = false

      // Load students for the class if we have class_id
      if (r.class_id && !this.students.length) {
        await this.fetchStudents('', 1, r.class_id, this.currentSessionId)
        // If still not found and there are more pages, keep searching
        if (!studentFound && this.studentPagination.last > 1) {
          console.log('Student not in first page, searching additional pages...')
          for (let page = 2; page <= this.studentPagination.last; page++) {
            await this.fetchStudents('', page, r.class_id, this.currentSessionId)
            if (this.students.some(s => s.id === r.student_id)) {
              studentFound = true
              break
            }
          }
        }
        // Check if our target student is in the list
        const targetStudent = this.students.find(s => s.id === r.student_id)
        if (!targetStudent && r.student_id) {
        // If not found, add the current student from the record to the list
        // so the select shows the name instead of the ID
          this.students.push({
            id: r.student_id,
            first_name: r.student_name,
            last_name: '',
            enrollment: [
              {
                school_class: { name: r.class_name },
                section: { name: r.section_name },
                roll_no: r.roll_no,
              },
            ],
          })
        }

        console.log('Student search result:', {
          studentId: r.student_id,
          found: !!targetStudent || !targetStudent, // Always true now
          studentsCount: this.students.length,
        })
      }

      this.showPenaltyFields = !!r.penalty_id

      this.$nextTick(() => {
        this.form.setFieldsValue({
          session_id: this.currentSessionId,
          class_id: r.class_id,
          student_id: r.student_id,
          incident_date: r.incident_date ? moment(r.incident_date) : null,
          category_id: r.category_id,
          description: r.description || '',
          penalty_id: r.penalty_id,
          penalty_details: r.penalty_details || '',
          severity: r.severity ? r.severity.toLowerCase() : 'minor',
          reported_by: r.reported_by_id,
        })
      })
    },

    handleSubmit() {
      this.form.validateFields((err, values) => {
        if (err) return
        this.submitting = true
        const payload = {
          ...values,
          incident_date: values.incident_date
            ? values.incident_date.format('YYYY-MM-DD')
            : null,
        }
        this.$http
          .put(`/api/student-discipline/${this.record.id}`, payload)
          .then(() => {
            this.$message.success('Record updated successfully!')
            this.$emit('updated')
            this.handleCancel()
          })
          .catch((error) => {
            const msg = (error && error.message)
              ? error.message
              : 'Failed to update record'
            this.$message.error(msg)
          })
          .finally(() => (this.submitting = false))
      })
    },

    handleCancel() {
      this.$emit('close')
    },
  },
}
</script>
